// src/hooks/useDocumentAttachment.ts

import { useRef, useCallback } from "react";
import { getDocumentFileBytes, getDocumentName } from "../helpers/word";
import { createLogger } from "../utils/logger";

const log = createLogger("DocumentAttachment");

/**
 * Minimal shape of the OutlookMailResult fields needed by this hook.
 * The full OutlookMailResult type lives in src/interfaces/outlookMailTypes.ts.
 * This avoids a hard dependency on that module during parallel development.
 */
interface OutlookMailResultForAttachment {
  status: "preview" | "success" | "error";
  attachOpenDocument?: boolean;
}

export interface AttachmentDetails {
  open_document_bytes: string;
  open_document_name: string;
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

/**
 * Manages the document attachment state machine for the Outlook mail tool.
 *
 * Uses a ref (not state) so the flag is immediately readable — no stale closures,
 * no waiting for the next render cycle. The flag is set during a streaming callback
 * and read in the next handleAnalyze call; a ref guarantees the latest value.
 *
 * State transitions:
 * - Preview with attachOpenDocument=true  → flag ON
 * - Preview with attachOpenDocument=false → flag OFF
 * - Success (send/draft completed)        → flag OFF (consumed)
 * - Error                                 → flag unchanged (user can retry)
 *
 * Known behavior: After a direct send/draft from the email preview card,
 * pendingRef may stay true until the next streaming outlook result resets it.
 * This is because direct sends go through OutlookMailSection (which doesn't
 * have access to this hook instance) rather than the streaming callback in
 * useChatAnalysis. The consequence is one unnecessary document fetch on the
 * next non-outlook chat request. This is acceptable (~800ms, ~85KB extra).
 */
export function useDocumentAttachment() {
  const pendingRef = useRef(false);

  const handleOutlookMailResult = useCallback((result: OutlookMailResultForAttachment) => {
    const prevState = pendingRef.current;
    if (result.status === "preview" && result.attachOpenDocument) {
      pendingRef.current = true;
    } else if (result.status === "preview" && !result.attachOpenDocument) {
      pendingRef.current = false;
    } else if (result.status === "success") {
      pendingRef.current = false; // consumed
    }
    // errors don't change the flag — user can retry
    log.info("Attachment state updated", {
      status: result.status,
      attachOpenDocument: result.attachOpenDocument,
      prevState,
      newState: pendingRef.current,
    });
  }, []);

  const getAttachmentDetailsIfNeeded = useCallback(async (): Promise<AttachmentDetails | null> => {
    log.info("getAttachmentDetailsIfNeeded called", { pending: pendingRef.current });
    if (!pendingRef.current) return null;

    try {
      const fileBytes = await getDocumentFileBytes();
      const name = getDocumentName();
      const base64 = arrayBufferToBase64(fileBytes);
      log.info("Document attachment fetched", {
        documentName: name,
        rawBytes: fileBytes.byteLength,
        base64Length: base64.length,
      });
      return {
        open_document_bytes: base64,
        open_document_name: name,
      };
    } catch (err) {
      log.error("Failed to fetch document attachment", { error: String(err) });
      return null;
    }
  }, []);

  /**
   * Unconditional variant — always fetches the current document as a base64
   * attachment regardless of pendingRef state.  Used by the interactive email
   * card when the user explicitly toggles "attach document" on.
   */
  const getAttachmentDetails = useCallback(async (): Promise<AttachmentDetails | null> => {
    log.info("getAttachmentDetails called (unconditional)");
    try {
      const fileBytes = await getDocumentFileBytes();
      const name = getDocumentName();
      const base64 = arrayBufferToBase64(fileBytes);
      log.info("Document attachment fetched", {
        documentName: name,
        rawBytes: fileBytes.byteLength,
        base64Length: base64.length,
      });
      return { open_document_bytes: base64, open_document_name: name };
    } catch (err) {
      log.error("Failed to fetch document attachment", { error: String(err) });
      return null;
    }
  }, []);

  return {
    pendingDocumentAttachment: pendingRef.current,
    handleOutlookMailResult,
    getAttachmentDetailsIfNeeded,
    getAttachmentDetails,
  } as const;
}
